/**
 * Prepares the photo to be uploaded on the CLIENT: centered square crop and
 * rescale.
 *
 * It does not replace the server's normalization —which is the one that rules,
 * strips the EXIF and guarantees the format— but it avoids uploading 5 MB from
 * the phone for the server to throw away. A 12 Mpx photo goes from ~4 MB to
 * ~40 KB before leaving the device.
 *
 * If something fails (browser without canvas, image that does not decode) it
 * returns the ORIGINAL file: the server will normalize it anyway. Preparing is
 * an optimization, not a requirement.
 */

/** Side of the square that is uploaded. Larger than the server's 256, so that
 *  it does the final rescale with a better filter and no sharpness is lost. */
const CLIENT_AVATAR_SIZE = 512;

/** Maximum size the server accepts (`MAX_INPUT_BYTES` in Rust). */
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/** Types the server knows how to decode. */
export const ACCEPTED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

type AvatarFileError = "too-large" | "unsupported-type";

/**
 * Checks what can be checked without decoding.
 *
 * It is only to give a USEFUL message before uploading: a `File`'s `type` is
 * set by the browser from the extension and can be faked, so the real
 * validation (magic bytes) is done by the server.
 */
export function validateAvatarFile(file: File): AvatarFileError | null {
  if (file.size > MAX_AVATAR_BYTES) {
    return "too-large";
  }
  if (file.type && !ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    return "unsupported-type";
  }
  return null;
}

/** Centered square crop: the largest box that fits in the image. */
export function centerSquare(width: number, height: number) {
  const side = Math.min(width, height);
  return {
    side,
    x: Math.floor((width - side) / 2),
    y: Math.floor((height - side) / 2),
  };
}

/**
 * Returns the file ready to upload. It never throws: on any problem it returns
 * the original.
 */
export async function prepareAvatarFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const { side, x, y } = centerSquare(bitmap.width, bitmap.height);

    const canvas = document.createElement("canvas");
    canvas.width = CLIENT_AVATAR_SIZE;
    canvas.height = CLIENT_AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return file;
    }
    ctx.drawImage(bitmap, x, y, side, side, 0, 0, CLIENT_AVATAR_SIZE, CLIENT_AVATAR_SIZE);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", 0.9);
    });
    if (!blob || blob.size === 0) {
      return file;
    }
    return new File([blob], "avatar.webp", { type: "image/webp" });
  } catch {
    // Browser without `createImageBitmap`, corrupt image, "tainted" canvas…
    // The server normalizes anyway; uploading the original is worse but works.
    return file;
  }
}
